import {
  createDefaultBlockEntityData,
  inferBlockEntityEditorKind
} from "../core/blockEntityData.ts";
import type {
  BannerPatternData,
  BlockEntityData,
  ContainerItemData,
  SignTextSide
} from "../core/blockEntityData.ts";
import { useEditorStore } from "../store/editorStore.ts";

const DYE_COLORS = [
  "black",
  "blue",
  "brown",
  "cyan",
  "gray",
  "green",
  "light_blue",
  "light_gray",
  "lime",
  "magenta",
  "orange",
  "pink",
  "purple",
  "red",
  "white",
  "yellow"
];

const COMMON_BANNER_PATTERNS = [
  "minecraft:stripe_bottom",
  "minecraft:stripe_top",
  "minecraft:stripe_left",
  "minecraft:stripe_right",
  "minecraft:stripe_center",
  "minecraft:stripe_middle",
  "minecraft:cross",
  "minecraft:straight_cross",
  "minecraft:border",
  "minecraft:circle",
  "minecraft:rhombus",
  "minecraft:gradient",
  "minecraft:gradient_up",
  "minecraft:bricks",
  "minecraft:creeper",
  "minecraft:skull",
  "minecraft:flower",
  "minecraft:globe",
  "minecraft:piglin"
];

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value));
}

function SignSideEditor({
  title,
  side,
  onChange
}: {
  title: string;
  side: SignTextSide;
  onChange: (next: SignTextSide) => void;
}) {
  return (
    <div className="block-entity-subsection">
      <div className="block-entity-subheading">{title}</div>
      <div className="sign-line-grid">
        {side.lines.map((line, index) => (
          <input
            key={index}
            type="text"
            value={line}
            maxLength={128}
            placeholder={"Line " + (index + 1)}
            onChange={(event) => {
              const lines = [...side.lines] as SignTextSide["lines"];
              lines[index] = event.target.value;
              onChange({ ...side, lines });
            }}
          />
        ))}
      </div>
      <div className="block-entity-inline-grid">
        <label>
          <span>Text color</span>
          <select
            value={side.color}
            onChange={(event) =>
              onChange({ ...side, color: event.target.value })
            }
          >
            {DYE_COLORS.map((color) => (
              <option key={color} value={color}>
                {color}
              </option>
            ))}
          </select>
        </label>
        <label className="block-entity-check">
          <input
            type="checkbox"
            checked={side.glowing}
            onChange={(event) =>
              onChange({
                ...side,
                glowing: event.target.checked
              })
            }
          />
          <span>Glowing text</span>
        </label>
      </div>
    </div>
  );
}

function BannerEditor({
  patterns,
  onChange
}: {
  patterns: BannerPatternData[];
  onChange: (patterns: BannerPatternData[]) => void;
}) {
  const updatePattern = (
    index: number,
    patch: Partial<BannerPatternData>
  ) => {
    const next = patterns.map((pattern, patternIndex) =>
      patternIndex === index
        ? { ...pattern, ...patch }
        : pattern
    );
    onChange(next);
  };

  return (
    <div className="block-entity-subsection">
      <div className="block-entity-subheading">
        Banner patterns · {patterns.length}/20
      </div>
      <div className="block-entity-stack">
        {patterns.map((pattern, index) => (
          <div className="banner-pattern-row" key={index}>
            <input
              list="banner-pattern-options"
              value={pattern.pattern}
              onChange={(event) =>
                updatePattern(index, {
                  pattern: event.target.value
                })
              }
            />
            <select
              value={pattern.color}
              onChange={(event) =>
                updatePattern(index, {
                  color: event.target.value
                })
              }
            >
              {DYE_COLORS.map((color) => (
                <option key={color} value={color}>
                  {color}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() =>
                onChange(
                  patterns.filter(
                    (_pattern, patternIndex) =>
                      patternIndex !== index
                  )
                )
              }
            >
              ×
            </button>
          </div>
        ))}
      </div>
      <datalist id="banner-pattern-options">
        {COMMON_BANNER_PATTERNS.map((pattern) => (
          <option key={pattern} value={pattern} />
        ))}
      </datalist>
      <button
        className="mini-action-button"
        type="button"
        disabled={patterns.length >= 20}
        onClick={() =>
          onChange([
            ...patterns,
            {
              pattern: "minecraft:stripe_bottom",
              color: "white"
            }
          ])
        }
      >
        Add pattern
      </button>
    </div>
  );
}

function ContainerEditor({
  items,
  onChange
}: {
  items: ContainerItemData[];
  onChange: (items: ContainerItemData[]) => void;
}) {
  const updateItem = (
    index: number,
    patch: Partial<ContainerItemData>
  ) => {
    onChange(
      items.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item
      )
    );
  };

  return (
    <div className="block-entity-subsection">
      <div className="block-entity-subheading">
        Container contents · {items.length}
      </div>
      <div className="block-entity-stack">
        {items.map((item, index) => (
          <div className="container-item-card" key={index}>
            <div className="container-item-main">
              <label>
                <span>Slot</span>
                <input
                  type="number"
                  min={0}
                  max={255}
                  value={item.slot}
                  onChange={(event) =>
                    updateItem(index, {
                      slot: Number(event.target.value)
                    })
                  }
                />
              </label>
              <label>
                <span>Item ID</span>
                <input
                  type="text"
                  value={item.id}
                  onChange={(event) =>
                    updateItem(index, {
                      id: event.target.value
                    })
                  }
                />
              </label>
              <label>
                <span>Count</span>
                <input
                  type="number"
                  min={1}
                  max={99}
                  value={item.count}
                  onChange={(event) =>
                    updateItem(index, {
                      count: Number(event.target.value)
                    })
                  }
                />
              </label>
              <button
                type="button"
                onClick={() =>
                  onChange(
                    items.filter(
                      (_item, itemIndex) => itemIndex !== index
                    )
                  )
                }
              >
                ×
              </button>
            </div>
            <label className="block-entity-wide-field">
              <span>Modern components SNBT</span>
              <input
                type="text"
                value={item.componentsSnbt ?? ""}
                placeholder="{'minecraft:custom_name':{text:'Key'}}"
                onChange={(event) =>
                  updateItem(index, {
                    componentsSnbt: event.target.value
                  })
                }
              />
            </label>
            <label className="block-entity-wide-field">
              <span>Legacy tag SNBT</span>
              <input
                type="text"
                value={item.legacyTagSnbt ?? ""}
                placeholder="{display:{Name:'...'}}"
                onChange={(event) =>
                  updateItem(index, {
                    legacyTagSnbt: event.target.value
                  })
                }
              />
            </label>
          </div>
        ))}
      </div>
      <button
        className="mini-action-button"
        type="button"
        onClick={() =>
          onChange([
            ...items,
            {
              slot: items.length,
              id: "minecraft:stone",
              count: 1
            }
          ])
        }
      >
        Add item
      </button>
    </div>
  );
}

export function BlockEntityEditor() {
  const revision = useEditorStore(
    (state) => state.worldRevision
  );
  const world = useEditorStore((state) => state.world);
  const selectedPosition = useEditorStore(
    (state) => state.selectedPosition
  );
  const setData = useEditorStore(
    (state) => state.setSelectedBlockEntityData
  );
  const initializeData = useEditorStore(
    (state) => state.initializeSelectedBlockEntityData
  );

  void revision;

  if (!selectedPosition) return null;

  const block = world.getBlockData(
    selectedPosition.x,
    selectedPosition.y,
    selectedPosition.z
  );
  if (!block) return null;

  const stored = world.getBlockEntityData(
    selectedPosition.x,
    selectedPosition.y,
    selectedPosition.z
  );
  const defaultData =
    createDefaultBlockEntityData(block.blockId);
  const data: BlockEntityData =
    clone(stored ?? defaultData ?? {});
  const kind = inferBlockEntityEditorKind(block.blockId);

  const update = (next: BlockEntityData) => {
    setData(next);
  };

  return (
    <section className="block-entity-editor">
      <div className="block-entity-editor-heading">
        <div>
          <strong>Block Entity</strong>
          <span>
            {kind
              ? kind.replace("_", " ")
              : "advanced SNBT"}
          </span>
        </div>
        {!stored && defaultData && (
          <button
            className="mini-action-button"
            type="button"
            onClick={initializeData}
          >
            Enable data
          </button>
        )}
      </div>

      {kind === "sign" && data.sign && (
        <>
          <SignSideEditor
            title="Front"
            side={data.sign.front}
            onChange={(front) =>
              update({
                ...data,
                sign: {
                  ...data.sign!,
                  front
                }
              })
            }
          />
          <SignSideEditor
            title="Back"
            side={data.sign.back}
            onChange={(back) =>
              update({
                ...data,
                sign: {
                  ...data.sign!,
                  back
                }
              })
            }
          />
          <label className="block-entity-check">
            <input
              type="checkbox"
              checked={data.sign.waxed}
              onChange={(event) =>
                update({
                  ...data,
                  sign: {
                    ...data.sign!,
                    waxed: event.target.checked
                  }
                })
              }
            />
            <span>Waxed / locked sign</span>
          </label>
        </>
      )}

      {kind === "banner" && data.banner && (
        <BannerEditor
          patterns={data.banner.patterns}
          onChange={(patterns) =>
            update({
              ...data,
              banner: {
                ...data.banner!,
                patterns
              }
            })
          }
        />
      )}

      {kind === "container" && data.container && (
        <>
          <ContainerEditor
            items={data.container.items}
            onChange={(items) =>
              update({
                ...data,
                container: {
                  ...data.container!,
                  items
                }
              })
            }
          />
          <div className="block-entity-inline-grid">
            <label>
              <span>Custom name</span>
              <input
                type="text"
                value={data.container.customName ?? ""}
                onChange={(event) =>
                  update({
                    ...data,
                    container: {
                      ...data.container!,
                      customName: event.target.value
                    }
                  })
                }
              />
            </label>
            <label>
              <span>Lock key</span>
              <input
                type="text"
                value={data.container.lock ?? ""}
                onChange={(event) =>
                  update({
                    ...data,
                    container: {
                      ...data.container!,
                      lock: event.target.value
                    }
                  })
                }
              />
            </label>
          </div>
          <label className="block-entity-wide-field">
            <span>Loot table</span>
            <input
              type="text"
              value={data.container.lootTable ?? ""}
              placeholder="minecraft:chests/simple_dungeon"
              onChange={(event) =>
                update({
                  ...data,
                  container: {
                    ...data.container!,
                    lootTable: event.target.value
                  }
                })
              }
            />
          </label>
        </>
      )}

      {kind === "player_head" && data.playerHead && (
        <div className="block-entity-subsection">
          <div className="block-entity-subheading">
            Player profile / skin
          </div>
          <div className="block-entity-inline-grid">
            <label>
              <span>Profile name</span>
              <input
                type="text"
                value={data.playerHead.name ?? ""}
                onChange={(event) =>
                  update({
                    ...data,
                    playerHead: {
                      ...data.playerHead!,
                      name: event.target.value
                    }
                  })
                }
              />
            </label>
            <label>
              <span>UUID</span>
              <input
                type="text"
                value={data.playerHead.uuid ?? ""}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                onChange={(event) =>
                  update({
                    ...data,
                    playerHead: {
                      ...data.playerHead!,
                      uuid: event.target.value
                    }
                  })
                }
              />
            </label>
          </div>
          <label className="block-entity-wide-field">
            <span>Skin texture URL</span>
            <input
              type="url"
              value={data.playerHead.textureUrl ?? ""}
              placeholder="https://textures.minecraft.net/texture/..."
              onChange={(event) =>
                update({
                  ...data,
                  playerHead: {
                    ...data.playerHead!,
                    textureUrl: event.target.value
                  }
                })
              }
            />
          </label>
          <label className="block-entity-wide-field">
            <span>Resolved texture value (Base64)</span>
            <textarea
              rows={3}
              value={data.playerHead.textureValue ?? ""}
              onChange={(event) =>
                update({
                  ...data,
                  playerHead: {
                    ...data.playerHead!,
                    textureValue: event.target.value
                  }
                })
              }
            />
          </label>
          <div className="block-state-note">
            On recent Java versions player profile names are not
            automatically resolved by the game. Use a resolved texture
            value or textures.minecraft.net URL for deterministic preview
            and export.
          </div>
        </div>
      )}

      <div className="block-entity-subsection">
        <div className="block-entity-subheading">
          Raw SNBT merge
        </div>
        <textarea
          className="block-entity-raw"
          rows={4}
          value={data.rawSnbt ?? ""}
          placeholder={'{CustomName:\'{"text":"Example"}\'}'}
          onChange={(event) =>
            update({
              ...data,
              rawSnbt: event.target.value
            })
          }
        />
        <div className="block-state-note">
          Advanced data is emitted as an additional /data merge block
          command after the structured fields.
        </div>
      </div>

      {stored && (
        <button
          className="mini-action-button danger-action"
          type="button"
          onClick={() => setData(null)}
        >
          Clear Block Entity data
        </button>
      )}
    </section>
  );
}
